import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Building2, AlertCircle } from 'lucide-react';
import { propertiesAPI } from '../services/api';
import HotelOnboardingForm from '../components/HotelOnboardingForm';
import { useAuth } from '../context/AuthContext';

const PropertyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  // Item 5 — gtm_sales & gtm_sales_leader can never enter edit mode (even via ?mode=edit URL).
  const restrictedFromEdit = ['gtm_sales', 'gtm_sales_leader'].includes((user?.role || '').toLowerCase());
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(
    !restrictedFromEdit && searchParams.get('mode') === 'edit' ? 'edit' : 'view'
  );

  useEffect(() => {
    if (id) {
      loadProperty();
    }
  }, [id]);

  const loadProperty = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await propertiesAPI.getById(id);
      setProperty(response.data);
    } catch (error) {
      console.error('Error loading property:', error);
      setError('Failed to load property. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    try {
      setSaving(true);
      await propertiesAPI.update(id, formData);
      setMode('view');
      loadProperty();
    } catch (error) {
      console.error('Error saving property:', error);
      alert('Failed to save property. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (mode === 'edit') {
      setMode('view');
      loadProperty();
    } else {
      navigate('/properties');
    }
  };

  const handleModeChange = (newMode) => {
    if (restrictedFromEdit) return; // view-only roles cannot switch to edit
    setMode(newMode);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading property...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={32} className="text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Error Loading Property</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            onClick={() => navigate('/properties')}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Back to Properties
          </button>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Property Not Found</h3>
          <p className="text-gray-500 mb-4">The property you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/properties')}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Back to Properties
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="property-details-page">
      {/* Header */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/properties')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="back-to-properties"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-white font-bold text-lg shadow-sm">
                {property.propertyName?.charAt(0)?.toUpperCase() || 'P'}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  {property.propertyName || 'Unnamed Property'}
                </h2>
                <p className="text-sm text-gray-500">
                  {mode === 'view' ? 'Property Details' : 'Editing Property'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Mode indicator */}
          <div className={`px-3 py-1.5 rounded-full text-sm font-medium ${
            mode === 'view' 
              ? 'bg-blue-100 text-blue-700' 
              : 'bg-yellow-100 text-yellow-700'
          }`}>
            {mode === 'view' ? 'View Mode' : 'Edit Mode'}
          </div>
        </div>
      </div>

      {/* Form */}
      <HotelOnboardingForm
        mode={mode}
        initialData={property}
        onSave={handleSave}
        onCancel={handleCancel}
        onModeChange={handleModeChange}
        saving={saving}
      />
    </div>
  );
};

export default PropertyDetails;
